import { activityEntrySchema, paginated, type ClientServer } from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  ChevronRight,
  CircleAlert,
  CirclePause,
  CirclePlay,
  History,
  LayoutGrid,
  Server,
  ShieldAlert,
  Sparkles,
  Unplug,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GameIcon } from '@/components/game-icon';
import { EmptyState, SectionHeader, PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { serverAddress, type CardState } from '@/components/server-card';
import { StatTile } from '@/components/stat-tile';
import { PowerStateBadge, ServerStatusBadge, StatusPill } from '@/components/status-badges';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { apiRequest } from '@/lib/api';
import { describeEvent, timeAgo } from '@/lib/format';
import { myServersQuery } from '@/lib/server-queries';
import { useServerLive } from '@/lib/server-socket';
import { useAuthenticatedSession } from '@/lib/session';

export const Route = createFileRoute('/_app/overview')({ component: Overview });

/**
 * The customer overview: your servers' live state at a glance, recent account activity and
 * the way into Hearth AI. Everything here is read from existing endpoints; the full,
 * filterable list stays on My Servers (/).
 */
function Overview() {
  const { user } = useAuthenticatedSession();
  const servers = useQuery(myServersQuery('mine'));
  const [states, setStates] = useState<Record<string, CardState>>({});
  const onState = useCallback(
    (id: string, state: CardState) =>
      setStates((s) => (s[id] === state ? s : { ...s, [id]: state })),
    [],
  );
  const list = useMemo(
    () => [...(servers.data?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [servers.data],
  );
  const counts = useMemo(() => {
    const known = list.map((s) => (s.identifier in states ? states[s.identifier] : undefined));
    const count = (pred: (v: CardState) => boolean) =>
      known.every((v) => v !== undefined && v !== null)
        ? known.filter((v) => pred(v!)).length
        : null;
    return {
      online: count((v) => v === 'running' || v === 'starting' || v === 'stopping'),
      offline: count((v) => v === 'offline'),
      attention: count((v) => v === 'unreachable' || v === 'unavailable'),
    };
  }, [list, states]);

  return (
    <>
      <PageHeader
        title="Overview"
        icon={LayoutGrid}
        description={`Welcome back, ${user.username}. Your servers and account at a glance.`}
      />
      <div className="grid gap-8">
        {!user.twoFactorEnabled && (
          <Card className="flex-row flex-wrap items-center gap-3 px-4 py-3">
            <ShieldAlert className="text-warning size-5 shrink-0" aria-hidden />
            <p className="min-w-0 flex-1 text-sm">
              <span className="font-medium">Two-factor authentication is off.</span>{' '}
              <span className="text-muted-foreground">
                Turn it on so a leaked password isn’t enough to reach your servers.
              </span>
            </p>
            <Button asChild size="sm" variant="outline">
              <Link to="/account/security">Set it up</Link>
            </Button>
          </Card>
        )}

        <section aria-label="Your servers">
          <SectionHeader
            title="Your servers"
            description="Live state from each server’s node."
            actions={
              <Button asChild size="sm" variant="ghost">
                <Link to="/">
                  My Servers <ChevronRight />
                </Link>
              </Button>
            }
          />
          <QueryState
            query={servers}
            skeleton={
              <div className="grid gap-3" aria-busy>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-20 rounded-xl" />
                  ))}
                </div>
                <Skeleton className="h-40 rounded-xl" />
              </div>
            }
          >
            {(data) =>
              data.data.length === 0 ? (
                <EmptyState icon={Server} title="No servers yet">
                  Servers you own or have been given access to will appear here.
                  {user.isAdmin ? '' : ' Ask your provider to set one up for you.'}
                </EmptyState>
              ) : (
                <div className="grid gap-3">
                  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <StatTile icon={Server} label="Servers" value={data.meta.total} />
                    <StatTile icon={CirclePlay} label="Online" value={counts.online ?? '…'} />
                    <StatTile icon={CirclePause} label="Offline" value={counts.offline ?? '…'} />
                    <StatTile
                      icon={CircleAlert}
                      label="Unavailable"
                      value={counts.attention ?? '…'}
                      detail="Unreachable, installing or suspended"
                    />
                  </div>
                  <ul className="bg-card shadow-card divide-y rounded-xl border">
                    {list.map((s) => (
                      <ServerRow key={s.identifier} server={s} onState={onState} />
                    ))}
                  </ul>
                  {data.meta.total > data.data.length && (
                    <p className="text-muted-foreground text-sm">
                      Showing {data.data.length} of {data.meta.total}.{' '}
                      <Link to="/" className="text-primary hover:underline">
                        See them all
                      </Link>
                    </p>
                  )}
                </div>
              )
            }
          </QueryState>
        </section>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <RecentActivity />
          <section aria-label="Hearth AI">
            <SectionHeader title="Hearth AI" />
            <Link
              to={'/ai' as never}
              className="group bg-card shadow-card hover:border-brand/50 focus-visible:ring-ring/50 flex items-start gap-3 rounded-xl border p-4 transition-colors outline-none focus-visible:ring-[3px]"
            >
              <span className="bg-brand/12 text-brand ring-brand/30 grid size-9 shrink-0 place-items-center rounded-lg ring-1 ring-inset">
                <Sparkles className="size-4" aria-hidden />
              </span>
              <span className="grid min-w-0 flex-1 gap-1">
                <span className="font-medium">Ask about your servers</span>
                <span className="text-muted-foreground text-sm">
                  Read logs and settings, find plugins and plan changes. Nothing changes until you
                  approve the plan.
                </span>
              </span>
              <ChevronRight
                className="text-subtle group-hover:text-foreground mt-0.5 size-4 shrink-0"
                aria-hidden
              />
            </Link>
          </section>
        </div>
      </div>
    </>
  );
}

function ServerRow({
  server,
  onState,
}: {
  server: ClientServer;
  onState: (identifier: string, state: CardState) => void;
}) {
  const live = useServerLive(server.status === 'suspended' ? null : server.identifier);
  const reported: CardState = server.status
    ? 'unavailable'
    : live.connection === 'error'
      ? 'unreachable'
      : live.state;
  useEffect(() => onState(server.identifier, reported), [onState, server.identifier, reported]);
  return (
    <li>
      <Link
        to="/server/$server"
        params={{ server: server.identifier }}
        className="hover:bg-elevated/40 focus-visible:ring-ring/50 flex items-center gap-3 px-4 py-3 transition-colors outline-none first:rounded-t-xl last:rounded-b-xl focus-visible:ring-[3px] focus-visible:ring-inset"
      >
        <GameIcon identity={server.profile.identity} size="sm" />
        <span className="grid min-w-0 flex-1 leading-tight">
          <span className="truncate text-sm font-medium">{server.name}</span>
          <span className="text-muted-foreground truncate text-xs">
            {server.profile.identity.name} ·{' '}
            <span className="font-mono">{serverAddress(server)}</span>
          </span>
        </span>
        {server.status ? (
          <ServerStatusBadge status={server.status} />
        ) : live.connection === 'error' ? (
          <StatusPill tone="destructive" title={live.error}>
            <Unplug className="size-3" aria-hidden /> Unreachable
          </StatusPill>
        ) : live.state ? (
          <PowerStateBadge state={live.state} />
        ) : (
          <StatusPill tone="muted" pulse>
            Connecting
          </StatusPill>
        )}
      </Link>
    </li>
  );
}

function RecentActivity() {
  const query = useQuery({
    queryKey: ['account', 'activity', 'recent'],
    queryFn: ({ signal }) =>
      apiRequest('/api/client/account/activity?page=1&perPage=6', {
        schema: paginated(activityEntrySchema),
        signal,
      }),
  });
  return (
    <section aria-label="Recent account activity">
      <SectionHeader
        title="Recent account activity"
        actions={
          <Button asChild size="sm" variant="ghost">
            <Link to="/account/activity" search={{}}>
              All activity <ChevronRight />
            </Link>
          </Button>
        }
      />
      <QueryState query={query} skeleton={<Skeleton className="h-48 rounded-xl" />}>
        {(data) =>
          data.data.length === 0 ? (
            <EmptyState icon={History} title="No activity yet" className="py-10">
              Sign-ins and changes to your account are recorded here.
            </EmptyState>
          ) : (
            <ul className="bg-card shadow-card divide-y rounded-xl border">
              {data.data.map((e) => (
                <li key={e.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <span className="min-w-0 flex-1 truncate">{describeEvent(e.event)}</span>
                  <time
                    dateTime={e.createdAt}
                    className="text-muted-foreground shrink-0 text-xs tabular-nums"
                  >
                    {timeAgo(e.createdAt)}
                  </time>
                </li>
              ))}
            </ul>
          )
        }
      </QueryState>
    </section>
  );
}
